import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Copy, Pencil, Plus, Send, Trash2 } from "lucide-react";
import { Button, Skeleton } from "@finora/ui";
import type { AdminOfferDto, AdminOfferInput, AdminOfferItem, OfferProductDto } from "@finora/api-client";
import { ApiError } from "@finora/api-client";
import { api } from "../../lib/api";

const inputCls = "h-9 w-full rounded-lg border border-border px-3 text-sm focus:border-brand focus:outline-none";
const labelCls = "mb-1 block text-xs font-medium text-slate-500";

const STATUS: Record<string, { label: string; cls: string }> = {
  DRAFT: { label: "Nacrt", cls: "bg-slate-100 text-slate-600" },
  SENT: { label: "Poslata", cls: "bg-blue-100 text-blue-700" },
  ACCEPTED: { label: "Prihvaćena", cls: "bg-green-100 text-green-700" },
  DECLINED: { label: "Odbijena", cls: "bg-red-100 text-red-700" },
};
const DURATION: Record<string, string> = { NONE: "—", UNDEFINED: "Neodređeno", MONTHS_12: "12 meseci", MONTHS_24: "24 meseca" };

function money(v: string | number) {
  return Number(v).toLocaleString("sr-RS", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function errMsg(e: unknown, fallback: string) {
  return e instanceof ApiError ? e.response.message : fallback;
}

export function OffersPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState<AdminOfferDto | "new" | null>(null);
  const [msg, setMsg] = useState<string | null>(null);

  const { data: offers, isLoading } = useQuery({ queryKey: ["admin-offers"], queryFn: () => api.listAdminOffers() });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ["admin-offers"] });

  const send = useMutation({
    mutationFn: (id: string) => api.sendAdminOffer(id),
    onSuccess: () => {
      setMsg("Ponuda je poslata mejlom.");
      refresh();
    },
    onError: (e) => setMsg(errMsg(e, "Slanje nije uspelo.")),
  });
  const del = useMutation({
    mutationFn: (id: string) => api.deleteAdminOffer(id),
    onSuccess: refresh,
    onError: (e) => setMsg(errMsg(e, "Brisanje nije uspelo.")),
  });

  return (
    <div className="space-y-4">
      <button
        onClick={() => navigate("/finora-admin")}
        className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"
      >
        <ArrowLeft className="h-4 w-4" aria-hidden /> Nazad
      </button>
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold text-slate-900">Ponude</h1>
        {!editing && (
          <Button onClick={() => setEditing("new")}>
            <Plus className="mr-1 h-4 w-4" aria-hidden /> Nova ponuda
          </Button>
        )}
      </div>

      {msg && <p className="rounded-lg bg-slate-100 px-3 py-2 text-sm text-slate-700">{msg}</p>}

      {editing ? (
        <OfferForm
          offer={editing === "new" ? null : editing}
          onDone={(m) => {
            setEditing(null);
            if (m) setMsg(m);
            refresh();
          }}
        />
      ) : isLoading ? (
        <Skeleton className="h-40 w-full" />
      ) : !offers || offers.length === 0 ? (
        <p className="rounded-xl border border-border bg-surface p-6 text-sm text-slate-500">Još nema ponuda.</p>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-border bg-surface shadow-subtle">
          <table className="w-full text-sm">
            <thead className="border-b border-border text-left text-xs text-slate-400">
              <tr>
                <th className="px-4 py-2">Broj</th>
                <th className="px-4 py-2">Kupac</th>
                <th className="px-4 py-2">Iznos (neto)</th>
                <th className="px-4 py-2">Ugovor</th>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2" />
              </tr>
            </thead>
            <tbody>
              {offers.map((o) => (
                <tr key={o.id} className="border-b border-border last:border-0 align-top">
                  <td className="px-4 py-2 font-medium text-slate-800">{o.number}</td>
                  <td className="px-4 py-2">
                    {o.companyId ? (
                      <Link className="text-brand hover:underline" to={`/finora-admin/companies/${o.companyId}`}>
                        {o.customerName}
                      </Link>
                    ) : (
                      <span>{o.customerName}</span>
                    )}
                    <div className="text-xs text-slate-400">{o.customerEmail}{!o.companyId ? " · novi kupac" : ""}</div>
                  </td>
                  <td className="px-4 py-2">
                    {money(o.total)} RSD <span className="text-xs text-slate-400">bez PDV-a</span>
                    <div className="text-xs text-slate-500">{money(o.totalWithVat)} RSD sa PDV-om ({o.vatPercent}%)</div>
                    <div className="text-xs text-slate-400">{o.billingPeriod === "YEARLY" ? "godišnje" : "mesečno"}</div>
                  </td>
                  <td className="px-4 py-2">{DURATION[o.durationOption]}</td>
                  <td className="px-4 py-2">
                    <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${STATUS[o.status]?.cls}`}>
                      {STATUS[o.status]?.label}
                    </span>
                    {o.acceptError && <div className="mt-1 max-w-xs text-xs text-red-600">{o.acceptError}</div>}
                    {o.status === "ACCEPTED" && !o.acceptError && (
                      <div className="mt-1 text-xs text-slate-400">faktura + ugovor kreirani</div>
                    )}
                  </td>
                  <td className="px-4 py-2">
                    <div className="flex justify-end gap-1">
                      {o.status !== "ACCEPTED" && (
                        <>
                          <Button size="sm" variant="secondary" disabled={send.isPending} onClick={() => send.mutate(o.id)}>
                            <Send className="mr-1 h-3.5 w-3.5" aria-hidden />
                            {o.status === "DRAFT" ? "Pošalji" : "Pošalji ponovo"}
                          </Button>
                          <Button size="sm" variant="secondary" onClick={() => setEditing(o)} aria-label="Izmeni">
                            <Pencil className="h-3.5 w-3.5" aria-hidden />
                          </Button>
                          <Button
                            size="sm"
                            variant="secondary"
                            aria-label="Obriši"
                            onClick={() => window.confirm(`Obrisati ponudu ${o.number}?`) && del.mutate(o.id)}
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                          </Button>
                        </>
                      )}
                      {o.status !== "DRAFT" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          aria-label="Kopiraj link"
                          onClick={() => {
                            void navigator.clipboard?.writeText(o.publicUrl);
                            setMsg("Link ponude je kopiran.");
                          }}
                        >
                          <Copy className="h-3.5 w-3.5" aria-hidden />
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function OfferForm({ offer, onDone }: { offer: AdminOfferDto | null; onDone: (msg?: string) => void }) {
  const { data: companies } = useQuery({ queryKey: ["super-admin-companies"], queryFn: () => api.listAllCompanies() });
  const { data: packages } = useQuery({ queryKey: ["admin-packages"], queryFn: () => api.listPackages() });
  const { data: modules } = useQuery({ queryKey: ["admin-modules"], queryFn: () => api.listModules() });

  const [existing, setExisting] = useState(offer ? !!offer.companyId : true);
  const [companyId, setCompanyId] = useState(offer?.companyId ?? "");
  const [name, setName] = useState(offer && !offer.companyId ? offer.customerName : "");
  const [taxId, setTaxId] = useState(offer && !offer.companyId ? offer.customerTaxId ?? "" : "");
  const [regNo, setRegNo] = useState(offer && !offer.companyId ? offer.customerRegNumber ?? "" : "");
  const [address, setAddress] = useState(offer && !offer.companyId ? offer.customerAddress ?? "" : "");
  const [contact, setContact] = useState(offer?.customerContactName ?? "");
  const [email, setEmail] = useState(offer?.customerEmail ?? "");
  const ex0 = offer?.customerExtra;
  const [street, setStreet] = useState(ex0?.street ?? "");
  const [houseNumber, setHouseNumber] = useState(ex0?.houseNumber ?? "");
  const [postalCode, setPostalCode] = useState(ex0?.postalCode ?? "");
  const [city, setCity] = useState(ex0?.city ?? "");
  const [phone, setPhone] = useState(ex0?.phone ?? "");
  const [bankAccounts, setBankAccounts] = useState<{ accountNumber: string; bankName?: string | null }[]>(
    ex0?.bankAccounts ?? [],
  );
  const [lookupMsg, setLookupMsg] = useState<string | null>(null);
  const lookup = useMutation({
    mutationFn: () => api.lookupCompanyByPib(taxId.trim()),
    onSuccess: (r) => {
      if (r.name) setName(r.name);
      if (r.registrationNumber) setRegNo(r.registrationNumber);
      setStreet(r.street ?? "");
      setHouseNumber(r.houseNumber ?? "");
      setPostalCode(r.postalCode ?? "");
      setCity(r.city ?? "");
      setAddress(r.address || [r.street, r.houseNumber, r.postalCode, r.city].filter(Boolean).join(" "));
      if (r.phone) setPhone(r.phone);
      if (r.owner) setContact(r.owner);
      if (r.email) setEmail(r.email);
      setBankAccounts(r.bankAccounts.map((b) => ({ accountNumber: b.accountNumber, bankName: b.bankName })));
      setLookupMsg("Podaci povučeni — proverite ih pre slanja.");
    },
    onError: (e) => setLookupMsg(errMsg(e, "Firma nije pronađena za ovaj PIB.")),
  });

  const [mode, setMode] = useState<"PACKAGE" | "MODULES">(offer ? (offer.packageId ? "PACKAGE" : "MODULES") : "PACKAGE");
  const [packageId, setPackageId] = useState(offer?.packageId ?? "");
  const [pkgPrice, setPkgPrice] = useState(offer?.packageId ? offer.items[0]?.price ?? "" : "");
  // moduleId -> cena
  const [picked, setPicked] = useState<Record<string, string>>(() => {
    const r: Record<string, string> = {};
    if (offer && !offer.packageId) for (const i of offer.items) if (i.moduleId) r[i.moduleId] = i.price;
    return r;
  });
  // "Ostalo": jednokratni artikli (iz baze ili sa WooCommerce sajta)
  type OtherRow = { key: string; name: string; sku: string; qty: string; unit: string; discount: string };
  const [others, setOthers] = useState<OtherRow[]>(() =>
    (offer?.items ?? [])
      .filter((i) => i.kind === "OTHER")
      .map((i, idx) => ({
        key: `o${idx}`,
        name: (i.name ?? "").replace(/ \(popust [\d.,]+%\)$/, "").replace(/ × \d+$/, ""),
        sku: i.sku ?? "",
        qty: String(i.qty ?? 1),
        unit: String(i.unit ?? i.price),
        discount: String(i.discount ?? 0),
      })),
  );
  const [prodQuery, setProdQuery] = useState("");
  const [prodResults, setProdResults] = useState<OfferProductDto[]>([]);
  const [prodMsg, setProdMsg] = useState<string | null>(null);
  const [prodBusy, setProdBusy] = useState(false);
  const addOther = (p: OfferProductDto) => {
    setOthers((o) => [...o, { key: `${p.id}-${Date.now()}`, name: p.name, sku: p.sku, qty: "1", unit: String(p.net), discount: "0" }]);
    setProdQuery("");
    setProdResults([]);
    setProdMsg(null);
  };
  const searchProd = async () => {
    setProdBusy(true);
    setProdMsg(null);
    try {
      const r = await api.searchOfferProducts(prodQuery);
      setProdResults(r);
      if (r.length === 0) setProdMsg("Nema u bazi. Kliknite „Povuci sa sajta“ ako je ovo šifra artikla.");
    } catch (e) {
      setProdMsg(errMsg(e, "Pretraga nije uspela."));
    } finally {
      setProdBusy(false);
    }
  };
  const pullWoo = async () => {
    setProdBusy(true);
    setProdMsg(null);
    try {
      addOther(await api.importOfferProductFromWoo(prodQuery.trim()));
    } catch (e) {
      setProdMsg(errMsg(e, "Artikal nije pronađen na sajtu."));
    } finally {
      setProdBusy(false);
    }
  };
  const rowAmount = (r: { qty: string; unit: string; discount: string }) =>
    Math.round((Number(r.qty) || 1) * (Number(r.unit.replace(",", ".")) || 0) * (1 - Math.min(100, Math.max(0, Number(r.discount.replace(",", ".")) || 0)) / 100) * 100) / 100;
  const otherTotal = others.reduce((s, r) => s + rowAmount(r), 0);

  const [billingPeriod, setBillingPeriod] = useState<"MONTHLY" | "YEARLY">(offer?.billingPeriod ?? "MONTHLY");
  const [duration, setDuration] = useState<AdminOfferInput["durationOption"]>(offer?.durationOption ?? "MONTHS_12");
  const [validUntil, setValidUntil] = useState(offer?.validUntil?.slice(0, 10) ?? "");
  const [note, setNote] = useState(offer?.note ?? "");

  const save = useMutation({
    mutationFn: async (sendNow: boolean) => {
      let items: AdminOfferItem[] = [];
      if (mode === "PACKAGE") {
        const pkg = packages?.find((p) => p.id === packageId);
        if (!pkg && others.length === 0) throw new Error("Izaberite paket ili dodajte artikle u „Ostalo“.");
        if (pkg) items = [{ name: `Paket ${pkg.name}`, price: pkgPrice || pkg.price }];
      } else {
        items = Object.entries(picked).map(([moduleId, price]) => ({
          moduleId,
          name: modules?.find((m) => m.id === moduleId)?.name ?? "Modul",
          price,
        }));
      }
      for (const r of others) {
        const qty = Number(r.qty) || 1;
        const unit = Number(r.unit.replace(",", ".")) || 0;
        const discount = Math.min(100, Math.max(0, Number(r.discount.replace(",", ".")) || 0));
        items.push({
          kind: "OTHER",
          name: `${r.name}${qty > 1 ? ` × ${qty}` : ""}${discount > 0 ? ` (popust ${discount}%)` : ""}`,
          sku: r.sku,
          qty,
          unit: String(unit),
          discount,
          price: String(rowAmount(r)),
        });
      }
      const payload: AdminOfferInput = {
        ...(existing
          ? { companyId }
          : {
              companyId: null,
              customerName: name,
              customerTaxId: taxId,
              customerRegNumber: regNo,
              customerAddress: address,
              customerContactName: contact,
              customerExtra: { street, houseNumber, postalCode, city, phone, bankAccounts },
            }),
        customerEmail: email || undefined,
        packageId: mode === "PACKAGE" && packageId ? packageId : null,
        items,
        billingPeriod,
        durationOption: duration,
        validUntil: validUntil || null,
        note: note || null,
      };
      if (existing && !companyId) throw new Error("Izaberite firmu.");
      const saved = offer ? await api.updateAdminOffer(offer.id, payload) : await api.createAdminOffer(payload);
      if (sendNow) {
        await api.sendAdminOffer(saved.id);
        return "Ponuda je sačuvana i poslata.";
      }
      return offer && offer.status !== "DRAFT" ? "Izmene su sačuvane." : "Ponuda je sačuvana kao nacrt.";
    },
    onSuccess: (m) => onDone(m),
  });

  const total =
    mode === "PACKAGE"
      ? Number(pkgPrice || packages?.find((p) => p.id === packageId)?.price || 0)
      : Object.values(picked).reduce((s, v) => s + Number(v || 0), 0);
  const grandTotal = total + otherTotal;

  return (
    <div className="space-y-5 rounded-xl border border-border bg-surface p-5 shadow-subtle">
      <h2 className="text-sm font-semibold text-slate-800">{offer ? `Izmena ponude ${offer.number}` : "Nova ponuda"}</h2>

      <div className="flex gap-2">
        <Button size="sm" variant={existing ? "primary" : "secondary"} onClick={() => setExisting(true)}>
          Postojeći korisnik
        </Button>
        <Button size="sm" variant={!existing ? "primary" : "secondary"} onClick={() => setExisting(false)}>
          Novi korisnik
        </Button>
      </div>

      {existing ? (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <label className={labelCls}>Firma</label>
            <select className={inputCls} value={companyId} onChange={(e) => setCompanyId(e.target.value)}>
              <option value="">— izaberi —</option>
              {companies?.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name} ({c.taxId})
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelCls}>Email za slanje (prazno = vlasnik firme)</label>
            <input className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
          <div className="sm:col-span-2">
            <label className={labelCls}>Naziv firme</label>
            <input className={inputCls} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>PIB</label>
            <div className="flex gap-2">
              <input className={inputCls} value={taxId} onChange={(e) => setTaxId(e.target.value)} />
              <Button size="sm" variant="secondary" disabled={lookup.isPending || taxId.trim().length < 8} onClick={() => lookup.mutate()}>
                {lookup.isPending ? "…" : "Pretraži"}
              </Button>
            </div>
            {lookupMsg && <p className="mt-1 text-xs text-slate-500">{lookupMsg}</p>}
          </div>
          <div>
            <label className={labelCls}>Matični broj</label>
            <input className={inputCls} value={regNo} onChange={(e) => setRegNo(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Adresa</label>
            <input className={inputCls} value={address} onChange={(e) => setAddress(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Kontakt osoba</label>
            <input className={inputCls} value={contact} onChange={(e) => setContact(e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <label className={labelCls}>Email</label>
            <input className={inputCls} value={email} onChange={(e) => setEmail(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Ulica</label>
            <input className={inputCls} value={street} onChange={(e) => setStreet(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Broj</label>
            <input className={inputCls} value={houseNumber} onChange={(e) => setHouseNumber(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Poštanski broj</label>
            <input className={inputCls} value={postalCode} onChange={(e) => setPostalCode(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Mesto</label>
            <input className={inputCls} value={city} onChange={(e) => setCity(e.target.value)} />
          </div>
          <div>
            <label className={labelCls}>Telefon</label>
            <input className={inputCls} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          {bankAccounts.length > 0 && (
            <div className="sm:col-span-3">
              <label className={labelCls}>Tekući računi</label>
              <div className="text-sm text-slate-700">
                {bankAccounts.map((b) => (
                  <div key={b.accountNumber}>
                    {b.accountNumber}
                    {b.bankName ? ` (${b.bankName})` : ""}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      <div className="space-y-3">
        <div className="flex gap-2">
          <Button size="sm" variant={mode === "PACKAGE" ? "primary" : "secondary"} onClick={() => setMode("PACKAGE")}>
            Paket
          </Button>
          <Button size="sm" variant={mode === "MODULES" ? "primary" : "secondary"} onClick={() => setMode("MODULES")}>
            Pojedinačni moduli
          </Button>
        </div>

        {mode === "PACKAGE" ? (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            <div>
              <label className={labelCls}>Paket</label>
              <select
                className={inputCls}
                value={packageId}
                onChange={(e) => {
                  setPackageId(e.target.value);
                  setPkgPrice(packages?.find((p) => p.id === e.target.value)?.price ?? "");
                }}
              >
                <option value="">— izaberi —</option>
                {packages?.filter((p) => p.isActive).map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} — {money(p.price)} RSD ({p.billingPeriod === "MONTHLY" ? "mesečno" : "godišnje"})
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className={labelCls}>Cena (neto, RSD) — možete promeniti</label>
              <input className={inputCls} value={pkgPrice} onChange={(e) => setPkgPrice(e.target.value)} />
            </div>
          </div>
        ) : (
          <div className="max-h-80 space-y-1 overflow-y-auto rounded-lg border border-border p-2">
            {modules?.filter((m) => m.isActive).map((m) => {
              const on = m.id in picked;
              const def = m.addonPrice ?? m.defaultPrice;
              return (
                <div key={m.id} className="flex items-center gap-3 rounded-md px-2 py-1 hover:bg-app-bg">
                  <input
                    type="checkbox"
                    checked={on}
                    onChange={(e) =>
                      setPicked((p) => {
                        const n = { ...p };
                        if (e.target.checked) n[m.id] = String(Number(def || 0));
                        else delete n[m.id];
                        return n;
                      })
                    }
                  />
                  <span className="flex-1 text-sm text-slate-700">{m.name}</span>
                  <span className="text-xs text-slate-400">cenovnik: {money(def || 0)}</span>
                  <input
                    disabled={!on}
                    className="h-8 w-28 rounded-lg border border-border px-2 text-right text-sm disabled:bg-slate-50"
                    value={on ? picked[m.id] : ""}
                    onChange={(e) => setPicked((p) => ({ ...p, [m.id]: e.target.value }))}
                  />
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="space-y-2">
        <label className={labelCls}>Ostalo — artikli (jednokratno, npr. video nadzor)</label>
        <div className="flex flex-wrap gap-2">
          <input
            className={inputCls + " flex-1"}
            placeholder="Naziv ili šifra artikla"
            value={prodQuery}
            onChange={(e) => setProdQuery(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && prodQuery.trim().length >= 2 && searchProd()}
          />
          <Button size="sm" variant="secondary" onClick={searchProd} disabled={prodBusy || prodQuery.trim().length < 2}>
            Traži
          </Button>
          <Button size="sm" variant="secondary" onClick={pullWoo} disabled={prodBusy || !prodQuery.trim()}>
            Povuci sa sajta
          </Button>
        </div>
        {prodMsg && <p className="text-xs text-amber-600">{prodMsg}</p>}
        {prodResults.length > 0 && (
          <div className="max-h-52 overflow-y-auto rounded-lg border border-border">
            {prodResults.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => addOther(p)}
                className="flex w-full items-center justify-between gap-3 px-3 py-1.5 text-left text-sm hover:bg-app-bg"
              >
                <span className="text-slate-700">
                  {p.name} <span className="text-xs text-slate-400">({p.sku})</span>
                </span>
                <span className="text-xs text-slate-500">{money(p.net)} RSD neto</span>
              </button>
            ))}
          </div>
        )}
        {others.length > 0 && (
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-app-bg text-xs text-slate-500">
                <tr>
                  <th className="px-2 py-1.5 text-left font-medium">Naziv</th>
                  <th className="px-2 py-1.5 text-right font-medium">Količina</th>
                  <th className="px-2 py-1.5 text-right font-medium">Cena (neto)</th>
                  <th className="px-2 py-1.5 text-right font-medium">Popust %</th>
                  <th className="px-2 py-1.5 text-right font-medium">Iznos (neto)</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {others.map((r) => {
                  const upd = (patch: Partial<OtherRow>) =>
                    setOthers((o) => o.map((x) => (x.key === r.key ? { ...x, ...patch } : x)));
                  return (
                    <tr key={r.key} className="border-t border-border">
                      <td className="px-2 py-1.5 text-slate-700">
                        {r.name} <span className="text-xs text-slate-400">{r.sku}</span>
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <input className="h-8 w-16 rounded-lg border border-border px-2 text-right" value={r.qty} onChange={(e) => upd({ qty: e.target.value })} />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <input className="h-8 w-28 rounded-lg border border-border px-2 text-right" value={r.unit} onChange={(e) => upd({ unit: e.target.value })} />
                      </td>
                      <td className="px-2 py-1.5 text-right">
                        <input className="h-8 w-16 rounded-lg border border-border px-2 text-right" value={r.discount} onChange={(e) => upd({ discount: e.target.value })} />
                      </td>
                      <td className="px-2 py-1.5 text-right font-medium text-slate-800">{money(rowAmount(r))} RSD</td>
                      <td className="px-2 py-1.5 text-right">
                        <button type="button" className="text-xs text-red-500 hover:underline" onClick={() => setOthers((o) => o.filter((x) => x.key !== r.key))}>
                          ukloni
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t border-border bg-app-bg">
                  <td colSpan={4} className="px-2 py-1.5 text-right text-xs text-slate-500">
                    Ukupno artikli (jednokratno, ne ulazi u ponavljajuću fakturu)
                  </td>
                  <td className="px-2 py-1.5 text-right font-semibold text-slate-800">{money(otherTotal)} RSD</td>
                  <td />
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
        <div>
          <label className={labelCls}>Naplata</label>
          <select className={inputCls} value={billingPeriod} onChange={(e) => setBillingPeriod(e.target.value as never)}>
            <option value="MONTHLY">Mesečno</option>
            <option value="YEARLY">Godišnje</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Ugovoreni period</label>
          <select className={inputCls} value={duration} onChange={(e) => setDuration(e.target.value as never)}>
            <option value="MONTHS_12">12 meseci</option>
            <option value="MONTHS_24">24 meseca</option>
            <option value="UNDEFINED">Neodređeno</option>
            <option value="NONE">Bez ugovorenog perioda</option>
          </select>
        </div>
        <div>
          <label className={labelCls}>Ponuda važi do</label>
          <input type="date" className={inputCls} value={validUntil} onChange={(e) => setValidUntil(e.target.value)} />
        </div>
        <div className="flex items-end text-sm font-semibold text-slate-800">Ukupno: {money(grandTotal)} RSD <span className="ml-1 text-xs font-normal text-slate-400">bez PDV-a</span></div>
      </div>

      <div>
        <label className={labelCls}>Napomena u mejlu (opciono)</label>
        <textarea className="w-full rounded-lg border border-border px-3 py-2 text-sm" rows={2} value={note} onChange={(e) => setNote(e.target.value)} />
      </div>

      {save.isError && (
        <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">
          {save.error instanceof Error && !(save.error instanceof ApiError) ? save.error.message : errMsg(save.error, "Čuvanje nije uspelo.")}
        </p>
      )}

      <div className="flex justify-end gap-2">
        <Button variant="secondary" onClick={() => onDone()}>
          Otkaži
        </Button>
        <Button variant="secondary" disabled={save.isPending} onClick={() => save.mutate(false)}>
          {save.isPending ? "Čuvam…" : "Sačuvaj"}
        </Button>
        <Button disabled={save.isPending} onClick={() => save.mutate(true)}>
          Sačuvaj i pošalji
        </Button>
      </div>
    </div>
  );
}
